// #287: bottom sheet with the details of one prospekt (leaflet) offer —
// opened by tapping the promo badge on a product tile. Shows regular price,
// promo price, validity window, retailer and the "Aus dem Prospekt" source
// label per the AC.
import React from 'react';
import type { ProspektOffer } from '../shared/prospekt-types';
import { useLanguage } from '../services/LanguageContext';

interface ProspektOfferSheetProps {
    offer: ProspektOffer;
    retailerName: string;
    onClose: () => void;
}

const ProspektOfferSheet: React.FC<ProspektOfferSheetProps> = ({ offer, retailerName, onClose }) => {
    const { t, language } = useLanguage();
    const locale = language === 'de' ? 'de-DE' : 'en-US';
    const euro = (cents: number) => (cents / 100).toLocaleString(locale, { style: 'currency', currency: 'EUR' });
    const day = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString(locale);

    return (
        <div className="fixed inset-0 z-[80]" role="dialog" aria-modal="true" aria-label={t('prospekt.sheetTitle')}>
            <div className="absolute inset-0 bg-dark/60 backdrop-blur-sm" onClick={onClose}></div>
            <div
                data-testid="prospekt-offer-sheet"
                className="absolute bottom-0 left-0 right-0 mx-auto w-full max-w-lg bg-white dark:bg-dark-card rounded-t-[2.5rem] border-t border-x border-slate-200 dark:border-dark-border shadow-2xl p-8 space-y-6 motion-safe:animate-fadeIn"
            >
                <div className="flex items-start justify-between">
                    <div className="space-y-1">
                        <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-amber-600 dark:text-amber-400 bg-amber-500/10 px-2.5 py-1 rounded-lg border border-amber-500/20">
                            <span aria-hidden="true">🏷️</span> {t('prospekt.source')}
                        </span>
                        <h3 className="text-2xl font-black text-slate-900 dark:text-white leading-tight">{offer.productName}</h3>
                        {offer.brand && <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">{offer.brand}</p>}
                    </div>
                    <button
                        onClick={onClose}
                        aria-label={t('prospekt.close')}
                        className="w-10 h-10 rounded-xl bg-slate-50 dark:bg-dark-border text-slate-400 hover:text-emerald-500 flex items-center justify-center transition-all"
                    >
                        <i className="fas fa-times" aria-hidden="true"></i>
                    </button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div className="p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-2xl">
                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">{t('prospekt.promoPrice')}</div>
                        <div className="text-2xl font-black text-emerald-500" data-testid="prospekt-sheet-price">{euro(offer.priceCents)}</div>
                        {offer.discountLabel && (
                            <div className="text-[10px] font-black bg-red-500 text-white px-1.5 py-0.5 rounded inline-block uppercase mt-1">{offer.discountLabel}</div>
                        )}
                    </div>
                    <div className="p-4 bg-slate-50 dark:bg-dark-border/30 border border-slate-100 dark:border-dark-border/50 rounded-2xl">
                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">{t('prospekt.regularPrice')}</div>
                        <div className="text-2xl font-black text-slate-400 line-through" data-testid="prospekt-sheet-regular">
                            {offer.regularPriceCents ? euro(offer.regularPriceCents) : '—'}
                        </div>
                    </div>
                </div>

                <dl className="space-y-3 text-sm">
                    <div className="flex items-center justify-between">
                        <dt className="font-bold text-slate-400 uppercase text-[11px] tracking-widest">{t('prospekt.retailer')}</dt>
                        <dd className="font-black text-slate-900 dark:text-white" data-testid="prospekt-sheet-retailer">{retailerName}</dd>
                    </div>
                    <div className="flex items-center justify-between">
                        <dt className="font-bold text-slate-400 uppercase text-[11px] tracking-widest">{t('prospekt.validity')}</dt>
                        <dd className="font-black text-slate-900 dark:text-white" data-testid="prospekt-sheet-validity">
                            {t('prospekt.validityRange').replace('{0}', day(offer.validFrom)).replace('{1}', day(offer.validTo))}
                        </dd>
                    </div>
                </dl>
            </div>
        </div>
    );
};

export default ProspektOfferSheet;
